import React from 'react';
import {useSelector} from 'react-redux';
import {AboutPage} from '~/entities/company-brochures';
import {selectIsUserOnBoard} from '~/entities/user';
import WelcomeScreenPage from '~/pages/WelcomePage/WelcomeScreenPage';
import {
    AddressVerificationPage,
    ChangePasswordPage,
    KYCPage,
    StuckPage,
    SuccessfullyChangedPasswordPage,
} from '~/pages/old-onboarding/';
import {SettingsPage, SupportFormPage, SupportPage} from '~/processes/app-maintenance';
import {CardRoutes} from '~/processes/cards';
import {SellTxPage} from '~/processes/crypto-sell-and-buy';
import {
    createTrTxReceiveRoute,
    createTrTxSendRoute,
    OwnWalletPage,
    ReceivingTrTxPage,
    SendTrTxContinuationPage,
    SendTxPage,
} from '~/processes/crypto-send-and-receive';
import {DemoHomePages, HomePage} from '~/processes/dashboard';
import {HomePagePaths} from '~/processes/dashboard/HomePage';
import {DocumentsPage} from '~/processes/documents';
import {ForgotPasswordPage, LoginPage, SignupPage} from '~/processes/onboarding';
import {CloseAccountPage, ProfilePage} from '~/processes/profile';
import {ReportsPage} from '~/processes/reports';
import {TransactionsPage} from '~/processes/transactions';
import {selectTotalBalance} from '~/redux/totalBalance/totalBalanceSelectors';
import {dashboardCategories, selectDemoTotal} from '~/root/impl/dashboard/DashboardCategories';
import {defineRoutes} from '~/root/route-utils/defineRoutes';
import {createUseResourceSuspense} from '~/shared/store/hooks';
import {BuyTxPage} from '../processes/crypto-sell-and-buy';
import * as api from './api';
import {kycUserInfo} from './api/v1/userInfo';
import {demoAlerts, fullAlerts} from './impl//dashboard/DashboardAlerts';
import {
    AddressBasedGate,
    EthFeeWarningGate,
    FeatureGate,
    InverseAuthGate,
    KycGate,
    PreviewGate,
    RootGate,
} from './route-utils/AppGates';
import {TariffsPage} from './routes/account-plans';
import {FiatOperationRouter} from './routes/operations';
import registerCurrencySelectionRoutes from './routes/select-currency-to';

export const routes = defineRoutes(({route, group}) => {
    const publicRoute = route; // no need to check anything
    const featureRoute = route.with({Gate: FeatureGate});
    const previewRoute = route.with({Gate: PreviewGate});
    const authRoute = route.with({Gate: InverseAuthGate});
    const kycRoute = route.with({Gate: KycGate});
    const rootRoute = route.with({Gate: RootGate});

    rootRoute('/', () => <WelcomeScreenPage />);

    kycRoute('/signup/kyc', () => <KYCPage userInfo$={kycUserInfo.query} />);
    kycRoute('/signup/stuck', () => <StuckPage userInfo$={kycUserInfo.query} />);

    authRoute('/login', () => <LoginPage />);
    publicRoute('/forgot-password', () => <ForgotPasswordPage />);
    authRoute('/signup', () => <SignupPage />);

    publicRoute('/about', () => <AboutPage />);
    featureRoute('/support', () => <SupportPage />);
    publicRoute('/support/form', () => <SupportFormPage />);

    publicRoute('/successfully-changed', () => <SuccessfullyChangedPasswordPage />);

    registerCurrencySelectionRoutes(featureRoute);

    group({Gate: AddressBasedGate}, () => {
        featureRoute('/operation/exchange/crypto/:from/fiat/:to', props => <SellTxPage {...props} />);
        featureRoute('/operation/exchange/fiat/:from/crypto/:to', props => <BuyTxPage {...props} />);
        featureRoute('/operation/withdraw/crypto/:accountId/wallet/*', props => (
            <SendTxPage accountType='crypto' {...props} />
        ));
        featureRoute('/operation/topup/crypto/:accountId/wallet/*', props => (
            <OwnWalletPage accountId={props.accountId} useWalletResource={api.useCryptoAccountResource} />
        ));

        featureRoute(createTrTxReceiveRoute('crypto', ':orderId'), props => (
            <ReceivingTrTxPage orderId={props.orderId} alerts$={api.alerts.query} />
        ));
        featureRoute(createTrTxSendRoute('crypto', ':trtId'), props => (
            <SendTrTxContinuationPage trtxId={props.trtId} />
        ));

        group({Gate: EthFeeWarningGate}, () => {
            featureRoute('/operation/exchange/stablecoin/:from/fiat/:to', props => <SellTxPage {...props} />);
            featureRoute('/operation/exchange/fiat/:from/stablecoin/:to', props => <BuyTxPage {...props} />);
            featureRoute('/operation/withdraw/stablecoin/:accountId/wallet/*', props => (
                <SendTxPage accountType='stablecoin' {...props} />
            ));
            featureRoute('/operation/topup/stablecoin/:accountId/wallet/*', props => (
                <OwnWalletPage accountId={props.accountId} useWalletResource={api.useTokenAccountResource} />
            ));

            featureRoute(createTrTxReceiveRoute('stablecoin', ':orderId'), props => (
                <ReceivingTrTxPage orderId={props.orderId} alerts$={api.alerts.query} />
            ));
            featureRoute(createTrTxSendRoute('stablecoin', ':trtId'), props => (
                <SendTrTxContinuationPage trtxId={props.trtId} />
            ));
        });
    });

    featureRoute('/operation/*', () => <FiatOperationRouter />);

    const to: HomePagePaths = {
        toSendCurrencySelection: '/select-currency-to/send',
        toReceiveCurrencySelection: '/select-currency-to/receive',
        toExchangeCurrencySelection: '/select-currency-to/exchange',
    };

    const useTotalBalance = createUseResourceSuspense(selectTotalBalance);

    previewRoute('/assets/*', () => (
        <HomePageSwitch
            home={
                <HomePage
                    paths={to}
                    alerts={fullAlerts}
                    categories={dashboardCategories}
                    useTotalBalance={useTotalBalance}
                />
            }
            demo={<DemoHomePages alerts={demoAlerts} selectZeroTotal={selectDemoTotal} />}
        />
    ));

    featureRoute('/transactions/:transactionId?', () => <TransactionsPage />);

    featureRoute('/cards/*', () => (
        <CardRoutes
            isPaymentRestrictedAtom={api.isPaymentRestricted.query}
            phoneAtom={api.phoneNumber.query}
            emailAtom={api.email.query}
        />
    ));

    featureRoute('/profile/account_closure', () => <CloseAccountPage phoneNumber={api.phoneNumber.query} />);
    featureRoute('/profile', () => <ProfilePage userProfileIdentity={api.userProfileIdentity.query} />);
    featureRoute('/address-verification', () => <AddressVerificationPage docsStatus$={api.addressDocsStatus.query} />);
    featureRoute('/change-password', () => <ChangePasswordPage />);
    featureRoute('/account-plans/*', () => <TariffsPage />);
    featureRoute('/settings/*', () => <SettingsPage brokerAccount={api.brokerAccount} />);

    featureRoute('/reports', () => <ReportsPage emailAtom={api.email.query} />);
    featureRoute('/documents', () => <DocumentsPage />);
});

function HomePageSwitch({demo, home}: {demo: React.JSX.Element; home: React.JSX.Element}) {
    const canAccessFeatures = useSelector(selectIsUserOnBoard);

    if (canAccessFeatures) {
        return home;
    } else {
        return demo;
    }
}
